import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, TextField, Button, Grid, Alert,
  CircularProgress, FormControlLabel, Switch, Divider,
  IconButton, Card, CardContent, Chip
} from '@mui/material';
import { Save, ArrowBack, Add, Delete, Person, School, Link as LinkIcon, LiveTv, Stop } from '@mui/icons-material';
import api from '../services/api';

const emptyPresenter = {
  name: '',
  title: '',
  institution: '',
  articleTitle: '',
  articleUrl: '',
  replayUrl: ''
};

const emptyModerator = {
  name: '',
  title: '',
  institution: ''
};

export default function SessionEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [session, setSession] = useState({
    title: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    type: 'webinar',
    location: 'Webinaire en ligne',
    videoLink: '',
    replayUrl: '',
    tags: ['SkinKnowledge'],
    presenters: [{ ...emptyPresenter }],
    moderators: [{ ...emptyModerator }],
    isPublished: true
  });

  useEffect(() => {
    if (!isNew) {
      fetchSession();
    }
  }, [id]);

  const fetchSession = async () => {
    try {
      const res = await api.get(`/sessions/${id}`);
      const data = res.data;
      setSession({
        ...data,
        date: data.date ? new Date(data.date).toISOString().split('T')[0] : '',
        presenters: data.presenters?.length > 0 ? data.presenters : [{ ...emptyPresenter }],
        moderators: data.moderators?.length > 0 ? data.moderators : [{ ...emptyModerator }]
      });
    } catch (err) {
      setError('Erreur lors du chargement de la session');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setSession({ ...session, [field]: value });
  };

  const handlePresenterChange = (index, field, value) => {
    const updated = [...session.presenters];
    updated[index] = { ...updated[index], [field]: value };
    setSession({ ...session, presenters: updated });
  };

  const addPresenter = () => {
    setSession({ ...session, presenters: [...session.presenters, { ...emptyPresenter }] });
  };

  const removePresenter = (index) => {
    if (session.presenters.length <= 1) return;
    const updated = session.presenters.filter((_, i) => i !== index);
    setSession({ ...session, presenters: updated });
  };

  const handleModeratorChange = (index, field, value) => {
    const updated = [...session.moderators];
    updated[index] = { ...updated[index], [field]: value };
    setSession({ ...session, moderators: updated });
  };

  const addModerator = () => {
    setSession({ ...session, moderators: [...session.moderators, { ...emptyModerator }] });
  };

  const removeModerator = (index) => {
    if (session.moderators.length <= 1) return;
    const updated = session.moderators.filter((_, i) => i !== index);
    setSession({ ...session, moderators: updated });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    try {
      const payload = {
        ...session,
        presenters: session.presenters.filter(p => p.name.trim()),
        moderators: session.moderators.filter(m => m.name.trim())
      };

      if (isNew) {
        await api.post('/sessions', payload);
        setSuccess('Session creee avec succes');
      } else {
        await api.put(`/sessions/${id}`, payload);
        setSuccess('Session mise a jour');
      }

      setTimeout(() => navigate('/sessions'), 1500);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const handleToggleLive = async () => {
    try {
      const res = await api.put(`/sessions/${id}/toggle-live`);
      setSession(prev => ({ ...prev, status: res.data.status }));
      setSuccess(res.data.status === 'live' ? 'Session en LIVE !' : 'Live arrêté');
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors du toggle live');
    }
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => navigate('/sessions')} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" sx={{ flexGrow: 1 }}>
          {isNew ? 'Nouvelle Session' : 'Modifier la Session'}
        </Typography>
        {!isNew && session.videoLink && (
          <Button
            variant="contained"
            startIcon={session.status === 'live' ? <Stop /> : <LiveTv />}
            onClick={handleToggleLive}
            sx={{
              bgcolor: session.status === 'live' ? '#2B2E35' : '#0350AC',
              '&:hover': { bgcolor: session.status === 'live' ? '#12151A' : '#023C83' },
              animation: session.status === 'live' ? 'pulse 2s infinite' : 'none',
              '@keyframes pulse': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.7 },
              },
            }}
          >
            {session.status === 'live' ? 'Arrêter le live' : 'Démarrer le live'}
          </Button>
        )}
      </Box>

      {session.status === 'live' && (
        <Alert severity="error" icon={<LiveTv />} sx={{ mb: 2, fontWeight: 'bold' }}>
          Cette session est actuellement EN LIVE
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <form onSubmit={handleSubmit}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Informations generales</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Titre de la session"
                value={session.title}
                onChange={(e) => handleChange('title', e.target.value)}
                required
                placeholder="SkinKnowledge du 19 fevrier 2026"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="date"
                label="Date"
                value={session.date}
                onChange={(e) => handleChange('date', e.target.value)}
                required
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={session.description}
                onChange={(e) => handleChange('description', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Lien video (live)"
                value={session.videoLink || ''}
                onChange={(e) => handleChange('videoLink', e.target.value)}
                placeholder="https://zoom.us/..."
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="URL Replay"
                value={session.replayUrl || ''}
                onChange={(e) => handleChange('replayUrl', e.target.value)}
                placeholder="https://youtube.com/..."
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={session.isPublished}
                    onChange={(e) => handleChange('isPublished', e.target.checked)}
                  />
                }
                label="Publiee"
              />
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              <Person sx={{ mr: 1, verticalAlign: 'middle' }} />
              Presentateurs ({session.presenters.length})
            </Typography>
            <Button startIcon={<Add />} onClick={addPresenter} size="small">
              Ajouter
            </Button>
          </Box>

          {session.presenters.map((presenter, index) => (
            <Card key={index} variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Presentateur {index + 1}
                  </Typography>
                  {session.presenters.length > 1 && (
                    <IconButton size="small" color="error" onClick={() => removePresenter(index)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Titre"
                      value={presenter.title}
                      onChange={(e) => handlePresenterChange(index, 'title', e.target.value)}
                      placeholder="Dr / Pr"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Nom complet"
                      value={presenter.name}
                      onChange={(e) => handlePresenterChange(index, 'name', e.target.value)}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Institution / Specialite"
                      value={presenter.institution}
                      onChange={(e) => handlePresenterChange(index, 'institution', e.target.value)}
                      placeholder="Cardiologue - CHU de Rouen"
                    />
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Titre de l'article"
                      value={presenter.articleTitle}
                      onChange={(e) => handlePresenterChange(index, 'articleTitle', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="URL PubMed"
                      value={presenter.articleUrl}
                      onChange={(e) => handlePresenterChange(index, 'articleUrl', e.target.value)}
                      placeholder="https://pubmed.ncbi.nlm.nih.gov/..."
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="URL Replay (YouTube)"
                      value={presenter.replayUrl || ''}
                      onChange={(e) => handlePresenterChange(index, 'replayUrl', e.target.value)}
                      placeholder="https://youtube.com/watch?v=..."
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}
        </Paper>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              <School sx={{ mr: 1, verticalAlign: 'middle' }} />
              Moderateurs ({session.moderators.length})
            </Typography>
            <Button startIcon={<Add />} onClick={addModerator} size="small">
              Ajouter
            </Button>
          </Box>

          {session.moderators.map((moderator, index) => (
            <Card key={index} variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Moderateur {index + 1}
                  </Typography>
                  {session.moderators.length > 1 && (
                    <IconButton size="small" color="error" onClick={() => removeModerator(index)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Titre"
                      value={moderator.title}
                      onChange={(e) => handleModeratorChange(index, 'title', e.target.value)}
                      placeholder="Dr / Pr"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Nom complet"
                      value={moderator.name}
                      onChange={(e) => handleModeratorChange(index, 'name', e.target.value)}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Institution / Specialite"
                      value={moderator.institution}
                      onChange={(e) => handleModeratorChange(index, 'institution', e.target.value)}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}
        </Paper>

        <Box display="flex" gap={2}>
          <Button
            variant="contained"
            type="submit"
            startIcon={saving ? <CircularProgress size={20} /> : <Save />}
            disabled={saving}
          >
            {saving ? 'Sauvegarde...' : 'Sauvegarder'}
          </Button>
          <Button variant="outlined" onClick={() => navigate('/sessions')}>
            Annuler
          </Button>
        </Box>
      </form>
    </Box>
  );
}
